/**
 * Request builders for profile endpoints (/api/me/*).
 * User items, user sources, and tags.
 */

function buildQueryString(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string] => entry[1] !== undefined
  );
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries).toString();
}

// === User Items ===

export function getUserItems(params: {
  page?: string;
  pageSize?: string;
  status?: string;
  type?: string;
  tagId?: string;
  q?: string;
  dismissed?: string;
}) {
  const qs = buildQueryString({
    'page[number]': params.page ?? '1',
    'page[size]': params.pageSize ?? '24',
    'filter[status]': params.status,
    'filter[type]': params.type,
    'filter[tagId]': params.tagId,
    'filter[q]': params.q,
    'filter[dismissed]': params.dismissed,
  });
  return { url: `/api/me/items${qs}` };
}

export function getUserItem(id: string) {
  return { url: `/api/me/items/${encodeURIComponent(id)}` };
}

export function updateUserItemStatus(id: string, status: string) {
  return {
    url: `/api/me/items/${encodeURIComponent(id)}`,
    method: 'PATCH' as const,
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ status }),
  };
}

export function addTagToUserItem(
  userItemId: string,
  body: { tagIds?: string[]; label?: string }
) {
  return {
    url: `/api/me/items/${encodeURIComponent(userItemId)}/tags`,
    method: 'POST' as const,
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  };
}

export function removeTagFromUserItem(userItemId: string, tagId: string) {
  return {
    url: `/api/me/items/${encodeURIComponent(userItemId)}/tags/${encodeURIComponent(tagId)}`,
    method: 'DELETE' as const,
  };
}

// === User Sources ===

export function getUserSources(params: {
  page?: string;
  pageSize?: string;
  dismissed?: string;
  platform?: string;
  itemType?: string;
}) {
  const qs = buildQueryString({
    'page[number]': params.page ?? '1',
    'page[size]': params.pageSize ?? '24',
    'filter[dismissed]': params.dismissed,
    'filter[platform]': params.platform,
    'filter[itemType]': params.itemType,
  });
  return { url: `/api/me/sources${qs}` };
}

export function getUserSource(id: string) {
  return {
    url: `/api/me/sources/${encodeURIComponent(id)}`,
  };
}

export function updateUserSourceDismissed(id: string, dismissed: boolean) {
  return {
    url: `/api/me/sources/${encodeURIComponent(id)}`,
    method: 'PATCH' as const,
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ dismissed }),
  };
}

export function deleteUserSourceByContentSourceId(contentSourceId: string) {
  return {
    url: `/api/me/sources/by-content/${encodeURIComponent(contentSourceId)}`,
    method: 'DELETE' as const,
  };
}

// === Tags ===

export function getTags() {
  return { url: '/api/me/tags' };
}

export function createTag(label: string) {
  return {
    url: '/api/me/tags',
    method: 'POST' as const,
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ label }),
  };
}

export function deleteTag(id: string) {
  return {
    url: `/api/me/tags/${encodeURIComponent(id)}`,
    method: 'DELETE' as const,
  };
}

// === Linked Accounts ===

export function getLinkedAccounts() {
  return { url: '/api/me/linked-accounts' };
}

export function deleteLinkedAccount(id: string) {
  return {
    url: `/api/me/linked-accounts/${encodeURIComponent(id)}`,
    method: 'DELETE' as const,
  };
}
